import { useMemo } from "react";
import { useFloorPlanStore } from "@/stores/floorPlan";

/**
 * SelectionAnnouncer - WCAG 2.4.11 Focus Appearance
 *
 * Announces selection changes to screen readers using aria-live.
 * Provides context about selected objects including name and position.
 * Visually hidden but accessible to assistive technology.
 */
export const SelectionAnnouncer: React.FC = () => {
  const { selectedId, volumes, walls, elements, getProductById } =
    useFloorPlanStore();

  // Derive announcement from state rather than using effect + setState
  const announcement = useMemo(() => {
    if (!selectedId) {
      return "No object selected. Use Tab to navigate objects.";
    }

    // Check if selected item is a volume (product)
    const selectedVolume = volumes.find((v) => v.id === selectedId);
    if (selectedVolume) {
      const product = getProductById(selectedVolume.productId);
      const name = product?.name || "Product";
      const pos = selectedVolume.position;
      return `${name} selected at position ${Math.round(pos.x)} centimeters by ${Math.round(pos.y)} centimeters. Use arrow keys to move, R to rotate, Delete to remove.`;
    }

    // Check if selected item is a wall
    const selectedWall = walls.find((w) => w.id === selectedId);
    if (selectedWall) {
      const length = Math.round(
        Math.hypot(
          selectedWall.end.x - selectedWall.start.x,
          selectedWall.end.y - selectedWall.start.y
        )
      );
      return `Wall selected, ${length} centimeters long. Press Delete to remove.`;
    }

    // Check if selected item is a structural element
    const selectedElement = elements.find((e) => e.id === selectedId);
    if (selectedElement) {
      const type = selectedElement.type === "window" ? "Window" : "Door";
      return `${type} selected, ${selectedElement.width} centimeters wide. Press Delete to remove.`;
    }

    return "Object selected.";
  }, [selectedId, volumes, walls, elements, getProductById]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
};

export default SelectionAnnouncer;
